import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { apiUrl, resolveUploadsPath } from "./config/api";
import { getAmortizationDefault } from "./utils/amortizationDefaults";

export default function CardModal({ open, onClose, item, onSave, isAdmin = false, tmcList = [], authHeaders = {} }) {
  const [editItem, setEditItem] = useState(item || {});
  const [isDirty, setIsDirty] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Справочники
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [showGroupEditor, setShowGroupEditor] = useState(false);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);

  const [selectedGroupId, setSelectedGroupId] = useState(item?.group_id ? String(item.group_id) : "");
  const [selectedCategoryId, setSelectedCategoryId] = useState(item?.category_id ? String(item.category_id) : "");
  const authorizedJsonHeaders = useMemo(() => ({ ...authHeaders, 'Content-Type': 'application/json' }), [authHeaders]);

  // Фото превью
  const [localPhotoUrl, setLocalPhotoUrl] = useState("");
  const fileInputRef = useRef();

  // --- Загрузка справочников (устойчивая) ---
  const reloadDicts = useCallback(async () => {
    try {
      const rg = await fetch(apiUrl("/api/tmc/groups"), { headers: authHeaders });
      const groupsData = rg.ok ? await rg.json() : [];
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch {
      setGroups([]);
    }

    try {
      const rc = await fetch(apiUrl("/api/tmc/categories"), { headers: authHeaders });
      const catsData = rc.ok ? await rc.json() : [];
      setCategories(Array.isArray(catsData) ? catsData : []);
    } catch {
      setCategories([]);
    }

    try {
      const ru = await fetch(apiUrl("/api/tmc/units"), { headers: authHeaders });
      const unitsData = ru.ok ? await ru.json() : [];
      const prepared = Array.isArray(unitsData)
        ? Array.from(new Set(unitsData.filter(Boolean).map(u => String(u).trim()))).filter(Boolean)
        : [];
      setUnits(prepared);
    } catch {
      setUnits([]);
    }
  }, [authHeaders]);
  useEffect(() => { reloadDicts(); }, [reloadDicts]);

  useEffect(() => {
    setEditItem(item || {});
    setSelectedGroupId(item?.group_id ? String(item.group_id) : "");
    setSelectedCategoryId(item?.category_id ? String(item.category_id) : "");
    setIsDirty(false);
    setLocalPhotoUrl("");
    setValidationError("");
  }, [item, open]);

  // Категории, относящиеся к выбранной группе
  const filteredCategories = Array.isArray(categories)
    ? categories.filter(cat => String(cat.groupId ?? cat.group_id) === selectedGroupId)
    : [];
  const unitOptions = useMemo(() => {
    const apiUnits = Array.isArray(units)
      ? units.filter(Boolean).map(u => String(u).trim()).filter(Boolean)
      : [];
    const fromList = Array.isArray(tmcList)
      ? tmcList.map(entry => entry?.unit).filter(Boolean).map(u => String(u).trim()).filter(Boolean)
      : [];
    const unique = Array.from(new Set([...apiUnits, ...fromList]));
    const current = editItem?.unit ? String(editItem.unit).trim() : "";
    if (current && !unique.includes(current)) unique.unshift(current);
    return unique;
  }, [units, tmcList, editItem?.unit]);

  const photoSrc = localPhotoUrl
    ? localPhotoUrl
    : (editItem.photo_url ? resolveUploadsPath(editItem.photo_url) : "");

  const isExpense = selectedGroupId === "1";

  if (!open || !item) return null;

  function handleChange(e) {
    const { name, value } = e.target;
    setEditItem(i => ({ ...i, [name]: value }));
    setIsDirty(true);
    if (validationError) setValidationError("");
  }
  function handleGroupChange(e) {
    setSelectedGroupId(e.target.value);
    setSelectedCategoryId("");
    setEditItem(i => ({ ...i, group_id: e.target.value, category_id: "" }));
    setIsDirty(true);
    if (validationError) setValidationError("");
  }
  function handleCategoryChange(e) {
    const value = e.target.value;
    setSelectedCategoryId(value);
    setEditItem(i => {
      const next = { ...i, category_id: value };
      if (!isExpense) {
        const category = categories.find(c => String(c.id) === String(value));
        const defaultPeriod = getAmortizationDefault(category?.name);
        if (defaultPeriod != null) next.amortization_period = defaultPeriod;
      }
      return next;
    });
    setIsDirty(true);
    if (validationError) setValidationError("");
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      setEditItem(i => ({ ...i, photoFile: file })); // сейчас игнорируется сервером
      setIsDirty(true);
      setLocalPhotoUrl(URL.createObjectURL(file));
      if (validationError) setValidationError("");
    }
  }

  function handleUnitChange(e) {
    const value = e.target.value;
    setEditItem(i => ({ ...i, unit: value }));
    setIsDirty(true);
    if (validationError) setValidationError("");
  }

  // ---- ВАЛИДАЦИЯ ----
  function validateFields() {
    const requiredFields = [
      { key: "name", label: "Наименование" },
      { key: "group_id", label: "Группа" },
      { key: "unit", label: "Ед. изм." },
      { key: "price", label: "Цена" },
      { key: "category_id", label: "Категория" },
    ];
    const emptyFields = requiredFields.filter(f => {
      let val = editItem[f.key];
      if (f.key === "price") return !val || isNaN(Number(val)) || Number(val) <= 0;
      return !val || (typeof val === "string" && val.trim() === "");
    });
    return emptyFields.map(f => f.label);
  }

  function handleSave() {
    const isNew = !editItem.id;
    let itemToSave = { ...editItem };

    // Валидация
    const notFilled = validateFields();
    if (notFilled.length > 0) {
      setValidationError(`Заполните обязательные поля: ${notFilled.join(", ")}`);
      return;
    }

    // Присваиваем code только при создании
    if (isNew) {
      const maxCode = tmcList.length ? Math.max(...tmcList.map(i => Number(i.code) || 0)) : 0;
      itemToSave.code = maxCode + 1;
    }

    // Пока отправляем только JSON (без загрузки фото)
    const payload = JSON.stringify(itemToSave);

    fetch(apiUrl('/api/tmc'), {
      method: 'POST',
      headers: authorizedJsonHeaders,
      body: payload
    })
      .then(res => res.json())
      .then(data => {
        onSave(data);
        setIsDirty(false);
        setLocalPhotoUrl("");
        setValidationError("");
        onClose();
      })
      .catch(err => {
        alert('Ошибка сохранения: ' + err);
      });
  }

  function handleClose() {
    if (isDirty) {
      if (!window.confirm("Есть несохранённые изменения. Закрыть без сохранения?")) return;
    }
    setLocalPhotoUrl("");
    setValidationError("");
    onClose();
  }

  // --- редакторы групп/категорий ---
  function handleAddGroup() {
    const name = prompt("Название новой группы:");
    if (!name) return;
    fetch(apiUrl('/api/tmc/groups'), {
      method: "POST",
      headers: authorizedJsonHeaders,
      body: JSON.stringify({ name })
    })
      .then(r => r.json())
      .then(() => reloadDicts());
  }
  function handleRemoveGroup(id) {
    if (!window.confirm("Удалить группу?")) return;
    fetch(apiUrl(`/api/tmc/groups/${id}`), { method: 'DELETE', headers: authHeaders })
      .then(res => res.json())
      .then(result => {
        if (result.ok) reloadDicts();
        else alert('Ошибка удаления: ' + (result.error || ''));
      })
      .catch(err => alert('Ошибка запроса: ' + err));
  }

  function handleAddCategory() {
    const name = prompt("Название новой категории:");
    if (!name) return;
    let groupId = prompt("ID группы для категории (например, 1 или 2):");
    if (!groupId) return;
    fetch(apiUrl('/api/tmc/categories'), {
      method: "POST",
      headers: authorizedJsonHeaders,
      body: JSON.stringify({ name, groupId: Number(groupId) })
    })
      .then(r => r.json())
      .then(() => reloadDicts());
  }
  function handleRemoveCategory(id) {
    if (!window.confirm("Удалить категорию?")) return;
    fetch(apiUrl(`/api/tmc/categories/${id}`), { method: 'DELETE', headers: authHeaders })
      .then(res => res.json())
      .then(result => {
        if (result.ok) reloadDicts();
        else alert('Ошибка удаления: ' + (result.error || ''));
      })
      .catch(err => alert('Ошибка запроса: ' + err));
  }

  function handlePriceChange(e) {
    let val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
    setEditItem(i => ({ ...i, price: val }));
    setIsDirty(true);
    if (validationError) setValidationError("");
  }
  function getFormattedPrice() {
    if (!editItem.price || isNaN(Number(editItem.price))) return "";
    const num = Number(editItem.price);
    return num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      background: "rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
    }}>
      <div style={{
        background: "#fff", padding: 32, borderRadius: 8,
        width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 4px 32px #0002",
        position: "relative"
      }}>
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: 12, right: 16,
            background: "none", border: "none", fontSize: 24, lineHeight: "20px", color: "#888", cursor: "pointer", zIndex: 2
          }}
          title="Закрыть"
        >&times;</button>

        <h2 style={{ marginTop: 0 }}>Карточка ТМЦ</h2>
        {editItem.code && (
          <div style={{ marginBottom: 12 }}>
            <b>Код:</b> {editItem.code}
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label>Наименование<br />
            <input name="name" value={editItem.name ?? ""} onChange={handleChange} style={inputStyle} />
          </label>
        </div>

        <div style={{ marginBottom: 12, display: "flex", alignItems: "center" }}>
          <label style={{ flex: 1 }}>Группа<br />
            <select value={selectedGroupId} onChange={handleGroupChange} style={inputStyle}>
              <option value="" disabled>Выберите группу...</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </label>
          {isAdmin && (
            <button style={dictBtn} onClick={() => setShowGroupEditor(true)}>
              Редактировать группы
            </button>
          )}
        </div>

        <div style={{ marginBottom: 12, display: "flex", alignItems: "center" }}>
          <label style={{ flex: 1 }}>Категория<br />
            <select
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              style={inputStyle}
              disabled={!selectedGroupId}
            >
              <option value="" disabled>Выберите категорию...</option>
              {filteredCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          {isAdmin && (
            <button style={dictBtn} onClick={() => setShowCategoryEditor(true)}>
              Редактировать категории
            </button>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Ед. изм.<br />
            <select
              name="unit"
              value={editItem.unit ?? ""}
              onChange={handleUnitChange}
              style={inputStyle}
              disabled={unitOptions.length === 0}
            >
              <option value="" disabled>
                {unitOptions.length ? "Выберите единицу..." : "Нет значений"}
              </option>
              {unitOptions.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </label>
          {unitOptions.length === 0 && (
            <div style={{ fontSize: 12, color: "#a33" }}>
              Нет доступных единиц измерения — сначала добавьте позиции ТМЦ
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>
            Цена<br />
            <div style={{ display: "flex", alignItems: "center" }}>
              <input
                name="price"
                value={editItem.price === undefined || editItem.price === "" ? "" : getFormattedPrice()}
                onChange={handlePriceChange}
                style={{ ...inputStyle, flex: "1 0 120px", marginRight: "8px" }}
                inputMode="decimal"
                pattern="[0-9]*"
                placeholder="Введите сумму"
              />
              <span style={{ color: "#222", fontSize: "16px", whiteSpace: "nowrap" }}>руб.</span>
            </div>
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Поставщик<br />
            <input name="supplier_link" value={editItem.supplier_link ?? ""} onChange={handleChange} style={inputStyle} />
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Комментарий<br />
            <input name="comment" value={editItem.comment ?? ""} onChange={handleChange} style={inputStyle} />
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Фото<br />
            <div style={{ display: "flex", alignItems: "center" }}>
              {photoSrc
                ? <img src={photoSrc} alt="Фото" style={{ width: 80, height: 80, objectFit: "cover", border: "1px solid #ccc", borderRadius: 4 }} />
                : <div style={{
                  width: 80, height: 80, background: "#f2f2f2", color: "#999",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1px solid #ccc", borderRadius: 4, fontSize: 16
                }}>Фото</div>
              }
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ marginLeft: 12 }}
                onChange={handleFileChange}
              />
            </div>
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Амортизация (мес.)<br />
            <input
              name="amortization_period"
              value={editItem.amortization_period ?? ""}
              onChange={handleChange}
              style={inputStyle}
              disabled={isExpense}
              placeholder={isExpense ? "Недоступно для расходников" : ""}
            />
          </label>
          {isExpense && <div style={{ color: "#c00", fontSize: 12 }}>Это расходный материал — амортизация не применяется</div>}
        </div>

        {validationError && (
          <div style={{ color: "#c00", marginBottom: 12, fontWeight: "bold" }}>
            {validationError}
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: "right" }}>
          <button onClick={handleClose} style={{ marginRight: 8 }}>Отмена</button>
          <button onClick={handleSave}>Сохранить</button>
        </div>

        {showGroupEditor && isAdmin && (
          <div style={modalEditorStyle}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>Редактирование групп</div>
            <ul style={{ marginBottom: 8 }}>
              {groups.map(g => (
                <li key={g.id} style={{ marginBottom: 4 }}>
                  {g.name}
                  <button style={dictBtn} onClick={() => handleRemoveGroup(g.id)}>🗑️</button>
                </li>
              ))}
            </ul>
            <button style={dictBtn} onClick={handleAddGroup}>➕ Добавить группу</button>
            <button style={{ marginLeft: 16 }} onClick={() => setShowGroupEditor(false)}>Закрыть</button>
          </div>
        )}

        {showCategoryEditor && isAdmin && (
          <div style={modalEditorStyle}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>Редактирование категорий</div>
            <ul style={{ marginBottom: 8 }}>
              {categories.map(c => (
                <li key={c.id} style={{ marginBottom: 4 }}>
                  {c.name} <span style={{ color: "#888" }}>({groups.find(g => String(g.id) === String(c.groupId ?? c.group_id))?.name})</span>
                  <button style={dictBtn} onClick={() => handleRemoveCategory(c.id)}>🗑️</button>
                </li>
              ))}
            </ul>
            <button style={dictBtn} onClick={handleAddCategory}>➕ Добавить категорию</button>
            <button style={{ marginLeft: 16 }} onClick={() => setShowCategoryEditor(false)}>Закрыть</button>
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "6px", fontSize: "15px", width: "100%", marginTop: "2px", marginBottom: "2px", boxSizing: "border-box"
};
const dictBtn = {
  marginLeft: 8, border: "none", background: "none", cursor: "pointer", fontSize: 16
};
const modalEditorStyle = {
  position: "absolute",
  left: 0, top: 0, right: 0, bottom: 0,
  background: "rgba(255,255,255,0.99)",
  borderRadius: 8,
  zIndex: 10,
  padding: "32px 24px",
  boxShadow: "0 4px 32px #0003"
};